import LegalLayout from "@/components/LegalLayout";
import { Link } from "react-router-dom";

export default function ForDesigners() {
  return (
    <LegalLayout
      title="Facetra for Independent Jewellery Designers"
      lastUpdated="February 13, 2026"
      testId="page-for-designers"
    >
      <p>
        Facetra is the design workflow tool for independent jewellery designers — the person
        who sketches, presents to clients, and hands off to a CAD bench or manufacturer. This
        page explains how Facetra fits into a solo designer's day-to-day.
      </p>

      <h2>The problem Facetra solves for independent designers</h2>
      <p>
        As an independent designer you're the whole studio in one person. Every hour spent
        rendering, drawing multi-view sheets or calculating BOMs is an hour not spent on
        client acquisition or new sketches. Outsourcing a render costs ₹2,000–₹5,000 per piece
        and takes 2–3 days — killing your ability to iterate live with a client.
      </p>

      <h2>How independent designers use Facetra</h2>

      <h3>1. Live client presentations</h3>
      <p>
        Show up to a client meeting with just your sketchbook and phone. Sketch the piece as
        you discuss it, snap a photo, and produce a photoreal render before the meeting ends.
        Iterate on the spot: <em>"emerald instead of ruby"</em>, <em>"add polki around the
        border"</em>, <em>"make the meena work green not red"</em>.
      </p>

      <h3>2. Portfolio building</h3>
      <p>
        Every render can be saved to a portfolio lookbook. Export as a PDF to send to prospects,
        or as an Instagram-ready image set. Build a 100-piece catalogue in a week instead of a
        year.
      </p>

      <h3>3. Manufacturer handoff</h3>
      <p>
        Send your karigar or CAD bench the exported Facetra CAD kit — multi-view sheet + BOM
        + 3D mesh + wall-thickness cheatsheet — instead of a hand-drawn sketch and a paragraph
        of WhatsApp instructions. Fewer clarification calls, fewer manufacturing errors.
      </p>

      <h3>4. Bill of Materials for accurate quotes</h3>
      <p>
        Facetra's BOM uses the live 22kt gold rate updated hourly, plus estimated stone
        weights and karigar hours. Your client quotes reflect today's cost, not last month's.
      </p>

      <h3>5. Refine iteratively without redoing the sketch</h3>
      <p>
        Lasso the region you want to change, describe the change in one sentence, get a new
        render in 60 seconds. Traditional workflow: redraw the whole piece by hand.
      </p>

      <h2>What you get vs. what you would spend</h2>
      <p>Typical solo-designer economics per piece:</p>
      <ul>
        <li><strong>Outsourced rendering</strong>: ₹2,000–₹5,000 → Facetra: ~₹12 per render</li>
        <li><strong>Multi-view drawings</strong>: 3–4 hrs of your own time → Facetra: 4 credits (~₹48) and 2 minutes</li>
        <li><strong>BOM calculation</strong>: 30–60 min per piece with a spreadsheet → Facetra: 1 credit (~₹12), auto-generated</li>
        <li><strong>3D mesh for CAD bench</strong>: ₹5,000+ commissioned model or 2 days learning Rhino → Facetra: 15 credits (~₹180) for Standard, 25 (~₹300) for HD</li>
      </ul>
      <p>
        A finished CAD kit that would cost you a full day and ₹8,000–₹15,000 costs ~₹384 and
        30 minutes with Facetra.
      </p>

      <h2>Which plan do most independent designers pick?</h2>
      <p>
        Most solo designers start with the <strong>Starter pack (₹499 for 50 credits)</strong>
        to try Facetra on a live client brief. Once they see the ROI, they upgrade to the{" "}
        <strong>Studio pack (₹7,000 for 500 credits)</strong> which covers ~15 finished CAD
        kits — enough for a full month of client work.
      </p>

      <h2>Frequently asked questions from independent designers</h2>

      <h3>I don't have a CAD operator — can Facetra work for me?</h3>
      <p>
        Yes. Many independent designers send the Facetra CAD kit directly to their karigar or
        to a freelance CAD service, skipping the traditional in-house CAD bench entirely. The
        3D mesh + BOM + multi-view sheet gives the manufacturer everything they need.
      </p>

      <h3>Can I customise Facetra's rendering style?</h3>
      <p>
        Yes — every render is prompt-controlled. Describe the aesthetic (
        <em>Rajwadi with meena work</em>, <em>modern minimalist gold band</em>, <em>temple
        motif chandbali</em>) and Facetra follows your lead. The lasso tool lets you refine
        specific regions after the initial render.
      </p>

      <h3>Do I need a graphics tablet or drawing skill?</h3>
      <p>
        No. Facetra accepts a phone photograph of a paper sketch. Many designers still prefer
        drawing on paper — Facetra is designed to complement that, not replace it.
      </p>

      <h3>Can I sell the renders as final marketing assets?</h3>
      <p>
        Yes. You own every render generated on your Facetra account and can use them however
        you like — client presentations, Instagram, website, print catalogue. See the{" "}
        <Link to="/terms">terms of service</Link>.
      </p>

      <h3>How much does it cost to try Facetra?</h3>
      <p>
        Free — every new account includes trial credits so you can test Facetra on a real
        client brief before paying.
      </p>

      <div className="mt-10 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-6 not-prose">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">For designers</div>
        <h3 className="font-display text-2xl mb-3">Sketch faster. Present better. Bill more.</h3>
        <p className="text-sm text-white/70 mb-4">
          Start with a free account and generate your first render + CAD kit in under 10 minutes.
        </p>
        <Link
          to="/signup"
          data-testid="for-designers-cta"
          className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-5 py-2.5 hover:bg-[#F3CE5A] transition"
        >
          Start free →
        </Link>
      </div>
    </LegalLayout>
  );
}
